import { useEffect, useState } from 'react';
import InputField from '../../../../../components/molecules/InputField';
import { updateObject } from '../../../../../helpers/obj';
import InputFieldSelect from '../../../../../components/molecules/InputField/InputFieldSelect';
import { beautify } from '../../../../../helpers/str';
import { getValue } from '../../../../../helpers/utils';
import InputFieldSwitch from '../../../../../components/molecules/InputField/InputFieldSwitch';
import { FIELD_SETTINGS, INPUT_TYPES, TYPE_ICONS, WIDGETS } from '../../../../../consts/builder';
import clsx from 'clsx';

const NodeLayout = ({ config, onChange }) => {
    const [forms, setForms] = useState(config?.forms || []);
    const [selectedForm, setSelectedForm] = useState(0);
    const [selectedField, setSelectedField] = useState(0);

    const handleDelete = (index) => {};

    const handleUpdateForm = (index, name, options) => {
        const newForms = [...forms];
        const values = options.map((row) => row.value);
        newForms[index] = updateObject(newForms[index], name, values);
        updateForms(newForms);
    };

    const updateForms = (newForms) => {
        setForms(newForms);
        if (onChange) {
            onChange(newForms);
        }
    };

    useEffect(() => {
        if (config?.forms) {
            setForms(config?.forms);
        }
    }, [config]);

    const conditionString = (condition) => {
        return Object.entries(condition)
            .map(([key, value]) => `${key} = ${value.const}`)
            .join(' and ');
    };

    const formTemplate = (formIndex, properties, parent_id, requiredFields) => {
        return (
            <>
                {Object.entries(properties)?.map(([name, row]) => {
                    const id = parent_id ? `${parent_id}.${name}` : name;
                    return (
                        <li
                            key={id}
                            id={id}>
                            {row.type === 'object' && (
                                <h5 className='fs-8 p-2 bg-light-warning border border-warning rounded'>
                                    {name}
                                </h5>
                            )}
                            {row.type !== 'object' && (
                                <div
                                    className={`p-2 mb-2 rounded ${
                                        selectedField === id
                                            ? 'bg-light-primary border border-primary'
                                            : 'border border-gray-300'
                                    }`}>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <h5
                                            className='fs-8 cursor-pointer m-0 d-flex align-items-center'
                                            onClick={() =>
                                                setSelectedField(selectedField === id ? -1 : id)
                                            }>
                                            <i className={clsx(TYPE_ICONS?.[row.type], 'me-2')}></i>
                                            {name}
                                        </h5>
                                        <div className='d-flex align-items-center'>
                                            <span
                                                className='cursor-pointer ms-2'
                                                onClick={() => handleDelete(formIndex)}>
                                                <i
                                                    className={clsx('fs-8 bi bi-asterisk', {
                                                        'text-danger':
                                                            requiredFields?.includes(name),
                                                    })}></i>
                                            </span>
                                            <span className='cursor-pointer ms-2'>
                                                <i className='bi bi-dash-circle text-danger'></i>
                                            </span>
                                            <span
                                                className='cursor-pointer ms-2'
                                                onClick={() =>
                                                    setSelectedField(selectedField === id ? -1 : id)
                                                }>
                                                <i
                                                    className={`bi bi-chevron-${
                                                        selectedField === id ? 'up' : 'down'
                                                    }`}></i>
                                            </span>
                                        </div>
                                    </div>
                                    {selectedField === id && (
                                        <div className='mt-2'>
                                            <div className='input-group'>
                                                <InputFieldSelect
                                                    config={{
                                                        name: 'type',
                                                        options: INPUT_TYPES,
                                                    }}
                                                    defaultValue={row.type}
                                                    className='form-select-xs'
                                                />
                                                <InputField
                                                    defaultValue={name}
                                                    config={{
                                                        name,
                                                        className: 'form-control-xs',
                                                        placeholder: 'Field Name',
                                                    }}
                                                />
                                            </div>
                                            <h5 className='mt-3 fs-8'>Title</h5>
                                            <InputField
                                                defaultValue={row?.title}
                                                config={{
                                                    name,
                                                    className: 'form-control-xs',
                                                    placeholder: 'Field Title',
                                                }}
                                            />
                                            {uiSchemaTemplate(formIndex, id)}
                                        </div>
                                    )}
                                </div>
                            )}
                            {row.properties && (
                                <ul>
                                    {formTemplate(formIndex, row?.properties, id, row.required)}
                                </ul>
                            )}
                            {row?.allOf?.map((conditionalRow) => {
                                return (
                                    <ul className='bg bg-light-danger rounded pe-2 pt-2 pb-1'>
                                        {conditionalRow?.if?.properties && (
                                            <span className='badge badge-danger mb-2'>
                                                {conditionString(conditionalRow?.if?.properties)}
                                            </span>
                                        )}
                                        {formTemplate(
                                            formIndex,
                                            conditionalRow?.then?.properties,
                                            id,
                                            conditionalRow?.then?.required
                                        )}
                                    </ul>
                                );
                            })}
                        </li>
                    );
                })}
            </>
        );
    };

    const uiSchemaTemplate = (formIndex, id) => {
        return (
            <>
                <h4 className='mt-3 fs-8'>Styles</h4>
                {Object.entries(getValue(config?.forms?.[formIndex].ui, id, true, {})).map(
                    ([uiKey, uiValue]) => {
                        return (
                            <div className='input-group mb-1'>
                                <InputFieldSelect
                                    config={{
                                        name: 'type',
                                        options: FIELD_SETTINGS,
                                    }}
                                    defaultValue={uiKey}
                                    className='form-select-xs'
                                />
                                {!['ui:widget', 'ui:label'].includes(uiKey) && (
                                    <InputField
                                        defaultValue={uiValue}
                                        config={{
                                            name: uiKey,
                                            className: 'form-control-xs',
                                        }}
                                    />
                                )}
                                {uiKey === 'ui:label' && (
                                    <div className='bg-white border border-gray-300 w-50 d-flex align-items-center ps-1 rounded-end'>
                                        <InputFieldSwitch
                                            defaultValue={uiValue}
                                            config={{ name: uiKey, hideLabel: true }}
                                        />
                                    </div>
                                )}
                                {uiKey === 'ui:widget' && (
                                    <>
                                        <InputFieldSelect
                                            config={{
                                                name: 'widget',
                                                options: WIDGETS,
                                            }}
                                            defaultValue={uiValue}
                                            className='form-select-xs'
                                        />
                                    </>
                                )}
                            </div>
                        );
                    }
                )}
            </>
        );
    };

    return (
        <>
            {forms?.map((form, formIndex) => {
                return (
                    <div
                        className='panel__section p-5'
                        key={formIndex}>
                        <div className='d-flex align-items-center justify-content-between'>
                            <div className='d-flex align-items-center'>
                                <i
                                    className={`bi bi-input-cursor-text me-1 ${
                                        selectedForm === formIndex ? 'text-info' : ''
                                    }`}></i>
                                <h5
                                    onClick={() =>
                                        setSelectedForm(selectedForm === formIndex ? -1 : formIndex)
                                    }
                                    className='fs-8 p-0 m-0 cursor-pointer'>
                                    Form {formIndex + 1}
                                </h5>
                            </div>
                            <div>
                                <span
                                    className='cursor-pointer ms-2'
                                    onClick={() => handleDelete(formIndex)}>
                                    <i className='bi bi-dash-circle-fill text-danger'></i>
                                </span>
                                <span
                                    className='cursor-pointer ms-2'
                                    onClick={() =>
                                        setSelectedForm(selectedForm === formIndex ? -1 : formIndex)
                                    }>
                                    <i
                                        className={`bi bi-chevron-${
                                            selectedForm === formIndex ? 'up' : 'down'
                                        }`}></i>
                                </span>
                            </div>
                        </div>
                        {selectedForm === formIndex && (
                            <div className='mt-5'>
                                <h5 className='fs-8'>Form Title</h5>
                                <InputField
                                    defaultValue={form?.schema?.title}
                                    config={{
                                        placeholder: 'Title',
                                        name: 'title',
                                        className: 'form-control-xs',
                                    }}
                                    onChange={(value) =>
                                        handleUpdateForm(formIndex, 'title', value)
                                    }
                                />
                                <ui className='tree'>
                                    {formTemplate(formIndex, form?.schema?.properties)}
                                </ui>
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
};
export default NodeLayout;
